import { Button, Dropdown, Menu } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCookies } from 'react-cookie';
import { ButtonType } from 'antd/lib/button';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CountryCode, getCookie, getDomainName, isUndefinedOrNullOrEmpty, setCookie } from 'common';
import { changeCountryCode, employerSelect, RootState, switchEmployerAdminAuth } from 'modules';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getEmployerInfo } from 'hooks';

export interface EmployerListDropdownProp {
  type?: ButtonType;
  btnClassName?: string;
}

export const EmployerListDropdown: React.FC<EmployerListDropdownProp> = ({ btnClassName, type }) => {
  const dispatch = useDispatch();
  const [, setCustomCookie, deleteCustomCookie] = useCookies();
  const [, setRefreshCookie] = useCookies(['refreshToken']);
  const [, setAccessTokenCookie, deleteAccessTokenCookie] = useCookies(['accessToken']);
  const [, setExpiredOnCookie] = useCookies(['expiredOn']);
  const [, setEmployerIdCookie] = useCookies(['employerId']);
  const { employers } = useSelector((state: RootState) => state.user);
  const [updatedEid, setUpdatedEid] = useState<string>(getCookie('employerId'));

  const switchEmployer = useCallback(
    (values: { eid: string; name: string; country: CountryCode }) => {
      if (updatedEid !== values.eid) {
        dispatch(
          switchEmployerAdminAuth(
            { eid: values.eid, countryCode: values.country },
            async (data: { refreshToken: string; expiredOn: number; accessToken: string; permissions: unknown }) => {
              dispatch(employerSelect());
              setRefreshCookie('refreshToken', data.refreshToken, {
                domain: getDomainName(),
                expires: dayjs.unix(data.expiredOn).toDate(),
              });
              // Clean up any leftover chunk cookies from previous sessions
              deleteAccessTokenCookie('accessToken');
              const existingChunks = parseInt(getCookie('accessTokenChunks') || '0', 10);
              for (let i = 0; i < existingChunks; i += 1) {
                deleteCustomCookie(`accessTokenChunk${i}`);
              }
              deleteCustomCookie('accessTokenChunks');
              if (data.accessToken.length <= 4096) {
                setAccessTokenCookie('accessToken', data.accessToken, {
                  domain: getDomainName(),
                  expires: dayjs.unix(data.expiredOn).toDate(),
                });
              } else {
                // Split access token into chunks
                const CHUNK_SIZE = 2048;
                const tokenChunks: string[] = [];
                for (let i = 0; i < data.accessToken.length; i += CHUNK_SIZE) {
                  tokenChunks.push(data.accessToken.slice(i, i + CHUNK_SIZE));
                }
                setCustomCookie('accessTokenChunks', tokenChunks.length.toString(), {
                  domain: getDomainName(),
                  expires: dayjs.unix(data.expiredOn).toDate(),
                });
                tokenChunks.forEach((chunk, index) => {
                  setCustomCookie(`accessTokenChunk${index}`, chunk, {
                    domain: getDomainName(),
                    expires: dayjs.unix(data.expiredOn).toDate(),
                  });
                });
              }
              setExpiredOnCookie('expiredOn', data.expiredOn, {
                domain: getDomainName(),
                expires: dayjs.unix(data.expiredOn).toDate(),
              });
              setEmployerIdCookie('employerId', values.eid, {
                domain: getDomainName(),
              });
              setCookie('permissions', JSON.stringify(data.permissions || []));
              setUpdatedEid(values.eid);

              // Get employer info for bulk import approval flag
              const employerInfoResp = await getEmployerInfo(values.eid);
              const { data: employerInfo } = employerInfoResp;
              setCookie('bulkImportApproval', employerInfo?.setting?.bulkImportApprovalFlag || false);
            }
          )
        );
      }
    },
    [dispatch, updatedEid, setAccessTokenCookie, setEmployerIdCookie, setExpiredOnCookie, setRefreshCookie]
  );

  const getCompanyName = useMemo(() => {
    const result = employers.find((item) => item.eid === updatedEid);
    if (isUndefinedOrNullOrEmpty(result)) return '-';

    dispatch(changeCountryCode({ code: result?.country || 'my' }));
    return result?.name ?? '-';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employers, updatedEid]);

  const employerListMenu = (
    <Menu selectedKeys={[updatedEid]}>
      {employers.map((item) => (
        <Menu.Item
          key={item.eid}
          onClick={() => switchEmployer({ eid: item.eid, name: item.name, country: item.country })}
        >
          {item.name}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown trigger={['click']} overlay={employerListMenu}>
      <Button className={btnClassName} type={type}>
        {getCompanyName} <DownOutlined />
      </Button>
    </Dropdown>
  );
};
